import React from 'react';

const GenericPivotTable = ({ config, data, referenceData }) => {

  const { pivotConfig, colors } = config;
  const { rows, columns, values, additionalFields, displayMode, tableColumns } = pivotConfig;

  const formatCurrency = (amount) => {
    // NaN kontrolü
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '0 €';
    }
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Pivot data'yı oluştur
  const createPivotData = (data, rows, columns, values) => {
    const pivotData = {};
    const rowKeys = new Set();
    const columnKeys = new Set();

    // Veriyi işle
    data.forEach(item => {
      const rowKey = rows.map(field => item[field] || 'Bilinmeyen').join('|');
      const columnKey = columns.map(field => item[field] || 'Bilinmeyen').join('|');
      
      rowKeys.add(rowKey);
      columnKeys.add(columnKey);

      if (!pivotData[rowKey]) {
        pivotData[rowKey] = {};
      }
      
      pivotData[rowKey][columnKey] = {
        value: item[values] || 0,
        originalItem: item
      };
    });

    // Reference data'dan tüm subgroup'ları ekle (veri olmasa bile)
    if (referenceData?.subgroups && columns.includes('subgroup_name')) {
      const maingroupName = data.length > 0 ? data[0].maingroup_name : '';
      const allSubgroups = referenceData.subgroups
        .filter(subgroup => subgroup.maingroup_name === maingroupName)
        .map(subgroup => subgroup.subgroup_name);
      
      allSubgroups.forEach(subgroup => {
        columnKeys.add(subgroup);
      });
    }

    return {
      data: pivotData,
      rowKeys: Array.from(rowKeys).sort(),
      columnKeys: Array.from(columnKeys).sort()
    };
  };

  const pivotData = createPivotData(data, rows, columns, values);

  // Toplamları hesapla
  const rowTotals = {};
  const columnTotals = {};
  let grandTotal = 0;

  pivotData.rowKeys.forEach(rowKey => {
    rowTotals[rowKey] = 0;
      
    pivotData.columnKeys.forEach(columnKey => {
        const cellData = pivotData.data[rowKey]?.[columnKey];
        const amount = cellData?.value || 0;
        
        // NaN kontrolü
        const validAmount = isNaN(amount) ? 0 : parseFloat(amount) || 0;
        
        rowTotals[rowKey] += validAmount;
        columnTotals[columnKey] = (columnTotals[columnKey] || 0) + validAmount;
        grandTotal += validAmount;
    });
  });

  // Normal tablo görünümü için render fonksiyonu
  const renderNormalTable = () => {
    if (!tableColumns || tableColumns.length === 0) return null;
    
    const columnLabels = {
      'market_name': 'Pazarlar',
      'country': 'Ülke',
      'city': 'Şehir',
      'agency': 'Acente',
      'summary': 'Fuar',
      'start_date': 'Başlangıç',
      'end_date': 'Bitiş',
      'person_name': 'Kişi',
      'fair_name': 'Fuar Adı',
      'quantity': 'Miktar',
      'vize': 'Vize',
      'kargo': 'Kargo',
      'otel': 'Otel',
      'ulaşım': 'Ulaşım',
      'extra': 'Extra',
      'harcirah': 'Harcirah',
      'stand': 'Stand'
    };

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              {tableColumns.map(column => (
                <th key={column} className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                  {columnLabels[column] || column.replace('_', ' ')}
                </th>
              ))}
              <th className={`px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                TOPLAM
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              const isEmpty = item.is_empty || false;
              
              // Toplam hesapla
              const totalAmount = parseFloat(item.budget_amount || 0);
              
              return (
                <tr key={item.id || index} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${isEmpty ? 'bg-gray-100 dark:bg-gray-800' : ''}`}>
                  {tableColumns.map(column => {
                    const value = item[column] || '';
                    const isAmountColumn = ['vize', 'kargo', 'otel', 'ulaşım', 'extra', 'harcirah', 'stand'].includes(column);
                    const displayValue = isAmountColumn ? formatCurrency(parseFloat(value) || 0) : (value === '' ? 'Boş' : value);
                    
                    return (
                      <td 
                        key={column}
                        className={`px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 ${
                          isAmountColumn ? 'text-right' : 'text-left'
                        } text-gray-900 dark:text-white`}
                      >
                        <span className={
                          isEmpty 
                            ? 'text-gray-400 dark:text-gray-500 italic' 
                            : isAmountColumn && parseFloat(value) > 0
                              ? 'font-semibold text-green-600 dark:text-green-400' 
                              : 'text-gray-900 dark:text-white'
                        }>
                          {displayValue}
                        </span>
                      </td>
                    );
                  })}
                  <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                    {formatCurrency(totalAmount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Ek alanları render et
  const renderAdditionalFields = (rowKey, cellData) => {
    if (!additionalFields || additionalFields.length === 0) return null;
    
    const originalItem = cellData?.originalItem;
    if (!originalItem) return null;

    return (
      <div className="mt-2 space-y-1">
        {additionalFields.map(field => {
          const value = originalItem[field] || '';
          const displayValue = value === '' ? 'Boş' : value;
          
          return (
            <div key={field} className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">{field.replace('_', ' ')}:</span> {displayValue}
            </div>
          );
        })}
      </div>
    );
  };

  // Workshop Road Show tablosu için özel render fonksiyonu
  const renderWorkshopTable = () => {
    if (!data || data.length === 0) return null;

    // Workshop Road Show verilerini filtrele
    const workshopData = data.filter(item => 
      item.maingroup_name === 'Work Shop Road Show' || item.maingroup_name === 'Workshop'
    );

    if (workshopData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Workshop Road Show verisi bulunamadı.
        </div>
      );
    }

    // Ülke ve market kombinasyonuna göre grupla (aynı market adı farklı ülkelerde ayrı gösterilsin)
    const marketCountryCombinations = [...new Set(
      workshopData.map(item => `${item.market_name || ''}|${item.country || ''}`).filter(Boolean)
    )];
    
    // Reference data'dan tüm subgroup'ları al (Workshop ana grubuna ait)
    const allSubgroups = referenceData?.subgroups?.filter(subgroup => 
      subgroup.maingroup_name === 'Work Shop Road Show' || subgroup.maingroup_name === 'Workshop'
    ).map(subgroup => subgroup.subgroup_name) || [];
    
    const subgroups = allSubgroups;
    
    const groupedData = marketCountryCombinations.reduce((acc, combination) => {
      const [market, country] = combination.split('|');
      const marketData = workshopData.filter(item => 
        (item.market_name || '') === market && (item.country || '') === country
      );
      const firstItem = marketData[0];
      
      const key = `${market}_${country}`;
      
      acc[key] = {
        market_name: market,
        country: country,
        city: firstItem?.city || '',
        agency: firstItem?.agency || '',
        departure: firstItem?.departure || '',
        arrival: firstItem?.arrival || '',
        subgroups: {}
      };
      
      // Tüm subgroup'ları göster (veri olmasa bile)
      subgroups.forEach(subgroup => {
        const subgroupData = marketData.filter(item => item.subgroup_name === subgroup);
        const totalAmount = subgroupData.reduce((sum, item) => sum + (parseFloat(item.budget_amount) || 0), 0);
        
        acc[key].subgroups[subgroup] = {
          amount: totalAmount
        };
      });
      
      return acc;
    }, {});

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">PAZARLAR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ÜLKE</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ŞEHİR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ACENTE</th>
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">GİDİŞ</th>
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">DÖNÜŞ</th>
              {subgroups.map(subgroup => (
                <th key={subgroup} className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                  {subgroup}
                </th>
              ))}
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">TOPLAM</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(groupedData).map((marketData, index) => {
              const totalAmount = Object.values(marketData.subgroups).reduce((sum, subgroupData) => sum + subgroupData.amount, 0);
              
              return (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.market_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.country}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.city}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.agency}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.departure ? new Date(marketData.departure).toLocaleDateString('tr-TR') : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.arrival ? new Date(marketData.arrival).toLocaleDateString('tr-TR') : ''}
                  </td>
                  {subgroups.map(subgroup => {
                    const subgroupData = marketData.subgroups[subgroup];
                    
                    return (
                      <td key={subgroup} className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                        <span className={
                          subgroupData.amount > 0 
                            ? 'font-semibold text-green-600 dark:text-green-400' 
                            : 'text-gray-400 dark:text-gray-500'
                        }>
                          {formatCurrency(subgroupData.amount || 0)}
                        </span>
                      </td>
                    );
                  })}
                  
                  {/* Toplam */}
                  <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 ${colors.total}`}>
                    {formatCurrency(totalAmount)}
                  </td>
                </tr>
              );
            })}
            
            {/* Grand Total Row */}
            <tr className={`${colors.total} hover:bg-opacity-80`}>
              <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600">
                TOPLAM
              </td>
              <td colSpan={5} className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600">
                {/* Ülke, Şehir, Acente, Gidiş, Dönüş sütunları için boş */}
              </td>
              {subgroups.map(subgroup => {
                // Her alt grup için toplam hesapla
                const subgroupTotal = Object.values(groupedData).reduce((total, marketData) => {
                  return total + (marketData.subgroups[subgroup]?.amount || 0);
                }, 0);
                
                return (
                  <td key={subgroup} className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600">
                    {formatCurrency(subgroupTotal)}
                  </td>
                );
              })}
              <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600">
                {formatCurrency(
                  Object.values(groupedData).reduce((grandTotal, marketData) => {
                    const marketTotal = Object.values(marketData.subgroups).reduce((sum, subgroupData) => sum + subgroupData.amount, 0);
                    return grandTotal + marketTotal;
                  }, 0)
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Promosyon tablosu için özel render fonksiyonu
  const renderPromotionTable = () => {
    if (!data || data.length === 0) return null;

    // Tüm subgroup'ları göster (veri olan market'ler için)
    const markets = [...new Set(data.map(item => item.market_name).filter(Boolean))];
    
    // Reference data'dan tüm subgroup'ları al (Promosyon ana grubuna ait)
    const allSubgroups = referenceData?.subgroups?.filter(subgroup => 
      subgroup.maingroup_name === 'Promosyon'
    ).map(subgroup => subgroup.subgroup_name) || [];
    
    const subgroups = allSubgroups;
    
    const groupedData = markets.reduce((acc, market) => {
      const marketData = data.filter(item => item.market_name === market);
      const firstItem = marketData[0];
      
      acc[market] = {
        market_name: market,
        country: firstItem?.country || '',
        city: firstItem?.city || '',
        agency: firstItem?.agency || '',
        subgroups: {}
      };
      
      // Tüm subgroup'ları göster (veri olmasa bile)
      subgroups.forEach(subgroup => {
        const subgroupData = marketData.filter(item => item.subgroup_name === subgroup);
        const totalQuantity = subgroupData.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
        const totalAmount = subgroupData.reduce((sum, item) => sum + (parseFloat(item.budget_amount) || 0), 0);
        
        acc[market].subgroups[subgroup] = {
          quantity: totalQuantity,
          amount: totalAmount
        };
      });
      
      return acc;
    }, {});

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">PAZARLAR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ÜLKE</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ŞEHİR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ACENTE</th>
              {subgroups.map(subgroup => (
                <th key={subgroup} colSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                  {subgroup}
                </th>
              ))}
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">TOPLAM</th>
            </tr>
            <tr className="bg-gray-50 dark:bg-gray-700">
              {subgroups.map(subgroup => (
                <React.Fragment key={subgroup}>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ADET</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">TUTAR</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.values(groupedData).map(marketData => {
              const totalAmount = Object.values(marketData.subgroups).reduce((sum, subgroupData) => sum + subgroupData.amount, 0);
              
              return (
                <tr key={marketData.market_name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  {/* Pazarlar, Ülke, Şehir, Acente */}
                  <td className="px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 text-left bg-white dark:bg-gray-800">
                    <span className="text-gray-900 dark:text-white font-medium">
                      {marketData.market_name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 text-left bg-white dark:bg-gray-800">
                    <span className="text-gray-900 dark:text-white">
                      {marketData.country || 'Boş'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 text-left bg-white dark:bg-gray-800">
                    <span className="text-gray-900 dark:text-white">
                      {marketData.city || 'Boş'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 text-left bg-white dark:bg-gray-800">
                    <span className="text-gray-900 dark:text-white">
                      {marketData.agency || 'Boş'}
                    </span>
                  </td>
                  
                  {/* Subgroup'lar */}
                  {subgroups.map(subgroup => {
                    const subgroupData = marketData.subgroups[subgroup] || { quantity: 0, amount: 0 };
                    
                    return (
                      <React.Fragment key={subgroup}>
                        <td className="px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 text-center bg-white dark:bg-gray-800">
                          <span className={
                            subgroupData.quantity > 0 
                              ? 'font-semibold text-blue-600 dark:text-blue-400' 
                              : 'text-gray-400 dark:text-gray-500'
                          }>
                            {subgroupData.quantity || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 text-center bg-white dark:bg-gray-800">
                          <span className={
                            subgroupData.amount > 0 
                              ? 'font-semibold text-green-600 dark:text-green-400' 
                              : 'text-gray-400 dark:text-gray-500'
                          }>
                            {formatCurrency(subgroupData.amount || 0)}
                          </span>
                        </td>
                      </React.Fragment>
                    );
                  })}
                  
                  {/* Toplam */}
                  <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 ${colors.total}`}>
                    {formatCurrency(totalAmount)}
                  </td>
                </tr>
              );
            })}
            
            {/* Toplam satırı */}
            <tr className="bg-gray-100 dark:bg-gray-600">
              <td colSpan="4" className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">TOPLAM</td>
              {subgroups.map(subgroup => (
                <React.Fragment key={subgroup}>
                  <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600">
                    {Object.values(groupedData).reduce((sum, marketData) => sum + (marketData.subgroups[subgroup]?.quantity || 0), 0)}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600">
                    {formatCurrency(Object.values(groupedData).reduce((sum, marketData) => sum + (marketData.subgroups[subgroup]?.amount || 0), 0))}
                  </td>
                </React.Fragment>
              ))}
              <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-gray-200 dark:bg-gray-500">
                {formatCurrency(Object.values(groupedData).reduce((sum, marketData) => 
                  sum + Object.values(marketData.subgroups).reduce((subSum, subgroupData) => subSum + subgroupData.amount, 0), 0
                ))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Fuar Detay tablosu için özel render fonksiyonu
  const renderFairDetailTable = () => {
    if (!data || data.length === 0) return null;

    // Fuar Detay verilerini filtrele
    const fairDetailData = data.filter(item => 
      item.maingroup_name === 'Fuar Detay'
    );

    if (fairDetailData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Fuar Detay verisi bulunamadı.
        </div>
      );
    }

    // Ülke ve market kombinasyonuna göre grupla (aynı market adı farklı ülkelerde ayrı gösterilsin)
    const marketCountryCombinations = [...new Set(
      fairDetailData.map(item => `${item.market_name || ''}|${item.country || ''}`).filter(Boolean)
    )];
    
    // Reference data'dan tüm subgroup'ları al (Fuar Detay ana grubuna ait)
    const allSubgroups = referenceData?.subgroups?.filter(subgroup => 
      subgroup.maingroup_name === 'Fuar Detay'
    ).map(subgroup => subgroup.subgroup_name) || [];
    
    const subgroups = allSubgroups;
    
    const groupedData = marketCountryCombinations.reduce((acc, combination) => {
      const [market, country] = combination.split('|');
      const marketData = data.filter(item => 
        (item.market_name || '') === market && (item.country || '') === country
      );
      const firstItem = marketData[0];
      
      const key = `${market}_${country}`;
      
      acc[key] = {
        market_name: market,
        country: country,
        city: firstItem?.city || '',
        agency: firstItem?.agency || '',
        start_date: firstItem?.start_date || '',
        end_date: firstItem?.end_date || '',
        subgroups: {}
      };
      
      // Tüm subgroup'ları göster (veri olmasa bile)
      subgroups.forEach(subgroup => {
        const subgroupData = marketData.filter(item => item.subgroup_name === subgroup);
        const totalAmount = subgroupData.reduce((sum, item) => sum + (parseFloat(item.budget_amount) || 0), 0);
        
        acc[key].subgroups[subgroup] = {
          amount: totalAmount
        };
      });
      
      return acc;
    }, {});

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">PAZARLAR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ÜLKE</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ŞEHİR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ACENTE</th>
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">BAŞLANGIÇ</th>
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">BİTİŞ</th>
              {subgroups.map(subgroup => (
                <th key={subgroup} className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                  {subgroup}
                </th>
              ))}
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">TOPLAM</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(groupedData).map((marketData, index) => {
              const totalAmount = Object.values(marketData.subgroups).reduce((sum, subgroupData) => sum + subgroupData.amount, 0);
              
              return (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.market_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.country}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.city}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.agency}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.start_date ? new Date(marketData.start_date).toLocaleDateString('tr-TR') : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.end_date ? new Date(marketData.end_date).toLocaleDateString('tr-TR') : ''}
                  </td>
                  {subgroups.map(subgroup => {
                    const subgroupData = marketData.subgroups[subgroup];
                    
                    return (
                      <td key={subgroup} className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                        <span className={
                          subgroupData.amount > 0 
                            ? 'font-semibold text-green-600 dark:text-green-400' 
                            : 'text-gray-400 dark:text-gray-500'
                        }>
                          {formatCurrency(subgroupData.amount || 0)}
                        </span>
                      </td>
                    );
                  })}
                  
                  {/* Toplam */}
                  <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 ${colors.total}`}>
                    {formatCurrency(totalAmount)}
                  </td>
                </tr>
              );
            })}
            
            {/* Grand Total Row */}
            <tr className={`${colors.total} hover:bg-opacity-80`}>
              <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600">
                TOPLAM
              </td>
              <td colSpan={5} className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600">
                {/* Ülke, Şehir, Acente, Başlangıç, Bitiş sütunları için boş */}
              </td>
              {subgroups.map(subgroup => {
                // Her alt grup için toplam hesapla
                const subgroupTotal = Object.values(groupedData).reduce((total, marketData) => {
                  return total + (marketData.subgroups[subgroup]?.amount || 0);
                }, 0);
                
                return (
                  <td key={subgroup} className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600">
                    {formatCurrency(subgroupTotal)}
                  </td>
                );
              })}
              <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600">
                {formatCurrency(
                  Object.values(groupedData).reduce((grandTotal, marketData) => {
                    const marketTotal = Object.values(marketData.subgroups).reduce((sum, subgroupData) => sum + subgroupData.amount, 0);
                    return grandTotal + marketTotal;
                  }, 0)
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Sales Call tablosu için özel render fonksiyonu
  const renderSalesCallTable = () => {
    if (!data || data.length === 0) return null;

    // Ülke ve market kombinasyonuna göre grupla (aynı market adı farklı ülkelerde ayrı gösterilsin)
    const marketCountryCombinations = [...new Set(
      data.map(item => `${item.market_name || ''}|${item.country || ''}`).filter(Boolean)
    )];
    
    // Reference data'dan tüm subgroup'ları al (Sales Call ana grubuna ait)
    const allSubgroups = referenceData?.subgroups?.filter(subgroup => 
      subgroup.maingroup_name === 'Sales Call'
    ).map(subgroup => subgroup.subgroup_name) || [];
    
    const subgroups = allSubgroups;
    
    const groupedData = marketCountryCombinations.reduce((acc, combination) => {
      const [market, country] = combination.split('|');
      const marketData = data.filter(item => 
        (item.market_name || '') === market && (item.country || '') === country
      );
      const firstItem = marketData[0];
      
      const key = `${market}_${country}`;
      
      acc[key] = {
        market_name: market,
        country: country,
        city: firstItem?.city || '',
        agency: firstItem?.agency || '',
        day_count: firstItem?.day_count || 0,
        pax: firstItem?.pax || 0,
        pp: firstItem?.pp || 0,
        subgroups: {}
      };
      
      // Tüm subgroup'ları göster (veri olmasa bile)
      subgroups.forEach(subgroup => {
        const subgroupData = marketData.filter(item => item.subgroup_name === subgroup);
        const totalAmount = subgroupData.reduce((sum, item) => sum + (parseFloat(item.budget_amount) || 0), 0);
        
        acc[key].subgroups[subgroup] = {
          amount: totalAmount
        };
      });
      
      return acc;
    }, {});

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">PAZARLAR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ÜLKE</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ŞEHİR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ACENTE</th>
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">GÜN</th>
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">PAX</th>
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">PP</th>
              {subgroups.map(subgroup => (
                <th key={subgroup} className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                  {subgroup}
                </th>
              ))}
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">TOPLAM</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(groupedData).map((marketData, index) => {
              const totalAmount = Object.values(marketData.subgroups).reduce((sum, subgroupData) => sum + subgroupData.amount, 0);
              
              return (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.market_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.country}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.city}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.agency}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.day_count}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {marketData.pax}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    {formatCurrency(marketData.pp || 0)}
                  </td>
                  {subgroups.map(subgroup => {
                    const subgroupData = marketData.subgroups[subgroup];
                    
                    return (
                      <td key={subgroup} className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                        <span className={
                          subgroupData.amount > 0 
                            ? 'font-semibold text-green-600 dark:text-green-400' 
                            : 'text-gray-400 dark:text-gray-500'
                        }>
                          {formatCurrency(subgroupData.amount || 0)}
                        </span>
                      </td>
                    );
                  })}
                  
                  {/* Toplam */}
                  <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 ${colors.total}`}>
                    {formatCurrency(totalAmount)}
                  </td>
                </tr>
              );
            })}
            
            {/* Toplam satırı */}
            <tr className="bg-gray-100 dark:bg-gray-600">
              <td colSpan="7" className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">TOPLAM</td>
              {subgroups.map(subgroup => (
                <td key={subgroup} className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600">
                  {formatCurrency(Object.values(groupedData).reduce((sum, marketData) => sum + (marketData.subgroups[subgroup]?.amount || 0), 0))}
                </td>
              ))}
              <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-gray-200 dark:bg-gray-500">
                {formatCurrency(Object.values(groupedData).reduce((sum, marketData) => 
                  sum + Object.values(marketData.subgroups).reduce((subSum, subgroupData) => subSum + subgroupData.amount, 0), 0
                ))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Yılbaşı Hediye tablosu için özel render fonksiyonu
  const renderChristmasGiftTable = () => {
    if (!data || data.length === 0) return null;

    // Reference data'dan tüm subgroup'ları al (Yılbaşı Hediye ana grubuna ait)
    const allSubgroups = referenceData?.subgroups?.filter(subgroup => 
      subgroup.maingroup_name === 'Yılbaşı Hediye'
    ).map(subgroup => subgroup.subgroup_name) || [];
    
    const subgroups = allSubgroups;
    
    // Her kayıt için ayrı satır oluştur (gruplama yapma)
    const tableData = data.map(item => ({
      id: item.id,
      market_name: item.market_name || '',
      agency: item.agency || '',
      person_name: item.person_name || '',
      summary: item.summary || '',
      subgroup_name: item.subgroup_name || '',
      budget_amount: parseFloat(item.budget_amount) || 0
    }));

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">PAZAR</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ACENTE</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">KİŞİ</th>
              <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">AÇIKLAMA</th>
              {subgroups.map(subgroup => (
                <th key={subgroup} className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                  {subgroup}
                </th>
              ))}
              <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">TOPLAM</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((item, index) => (
              <tr key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                  {item.market_name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                  {item.agency}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                  {item.person_name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                  {item.summary}
                </td>
                {subgroups.map(subgroup => {
                  const isCurrentSubgroup = item.subgroup_name === subgroup;
                  
                  return (
                    <td key={subgroup} className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                      <span className={
                        isCurrentSubgroup && item.budget_amount > 0
                          ? 'font-semibold text-green-600 dark:text-green-400' 
                          : 'text-gray-400 dark:text-gray-500'
                      }>
                        {isCurrentSubgroup ? formatCurrency(item.budget_amount) : '€0,00'}
                      </span>
                    </td>
                  );
                })}
                
                {/* Toplam */}
                <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 ${colors.total}`}>
                  {formatCurrency(item.budget_amount)}
                </td>
              </tr>
            ))}
            
            {/* Toplam satırı */}
            <tr className="bg-gray-100 dark:bg-gray-600">
              <td colSpan="4" className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">TOPLAM</td>
              {subgroups.map(subgroup => (
                <td key={subgroup} className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600">
                  {formatCurrency(tableData
                    .filter(item => item.subgroup_name === subgroup)
                    .reduce((sum, item) => sum + item.budget_amount, 0)
                  )}
                </td>
              ))}
              <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-gray-200 dark:bg-gray-500">
                {formatCurrency(tableData.reduce((sum, item) => sum + item.budget_amount, 0))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Temsil Ağırlama tablosu için özel render fonksiyonu (Otel gruplu, aylar sütun, PAX ve Tutar alt sütun)
  const renderRepresHosTable = () => {
    if (!data || data.length === 0) return null;

    // Otellere göre grupla
    const groupedByHotel = {};
    data.forEach(item => {
      const hotelName = item.hotel_name || 'Otel Belirtilmemiş';
      if (!groupedByHotel[hotelName]) {
        groupedByHotel[hotelName] = [];
      }
      groupedByHotel[hotelName].push(item);
    });

    // Tüm ayları göster (1-12) - veri olsun ya da olmasın
    const allMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    const allSubgroups = [...new Set(data.map(item => item.subgroup_name).filter(s => s))];

    const monthNames = {
      1: 'Ocak', 2: 'Şubat', 3: 'Mart', 4: 'Nisan', 5: 'Mayıs', 6: 'Haziran',
      7: 'Temmuz', 8: 'Ağustos', 9: 'Eylül', 10: 'Ekim', 11: 'Kasım', 12: 'Aralık'
    };

    return Object.keys(groupedByHotel).map(hotelName => {
      const hotelData = groupedByHotel[hotelName];

      return (
        <div key={hotelName} className="mb-8">
          {/* Otel Başlığı */}
          <div className="mb-4 pb-2 border-b-2 border-teal-500 dark:border-teal-400">
            <h3 className="text-xl font-bold text-teal-600 dark:text-teal-400 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {hotelName}
            </h3>
          </div>

          {/* Tablo */}
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className={`${colors.header}`}>
                <tr>
                  {/* İlk sütun: Alt Grup */}
                  <th rowSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                    Alt Grup
                  </th>
                  {/* Aylar - her ay 2 sütun kaplayacak (PAX ve Tutar) */}
                  {allMonths.map(month => (
                    <th key={month} colSpan="2" className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                      {monthNames[month]}
                    </th>
                  ))}
                  {/* Toplam - 2 sütun kaplayacak */}
                  <th rowSpan="2" className={`px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                    TOPLAM
                  </th>
                </tr>
                <tr>
                  {/* Her ay için PAX ve Tutar alt başlıkları */}
                  {allMonths.map(month => (
                    <React.Fragment key={`${month}-headers`}>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                        PAX
                      </th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                        Tutar
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allSubgroups.map(subgroup => {
                  let rowTotal = 0;

                  return (
                    <tr key={subgroup} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      {/* Alt Grup Adı */}
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600">
                        {subgroup}
                      </td>
                      {/* Her ay için PAX ve Tutar */}
                      {allMonths.map(month => {
                        const cellData = hotelData.find(item => 
                          item.subgroup_name === subgroup && item.budget_month === month
                        );
                        const pax = cellData?.pax || 0;
                        const amount = cellData?.budget_amount || 0;
                        rowTotal += parseFloat(amount) || 0;

                        return (
                          <React.Fragment key={`${subgroup}-${month}`}>
                            {/* PAX Sütunu */}
                            <td className="px-2 py-3 text-sm text-center border border-gray-200 dark:border-gray-600 bg-blue-50 dark:bg-blue-900/20">
                              <span className="text-blue-600 dark:text-blue-400 font-medium">
                                {pax > 0 ? pax : '-'}
                              </span>
                            </td>
                            {/* Tutar Sütunu */}
                            <td className="px-2 py-3 text-sm text-center border border-gray-200 dark:border-gray-600">
                              <span className={amount > 0 ? 'font-semibold text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                                {amount > 0 ? formatCurrency(amount) : '-'}
                              </span>
                            </td>
                          </React.Fragment>
                        );
                      })}
                      {/* Satır Toplamı */}
                      <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                        {formatCurrency(rowTotal)}
                      </td>
                    </tr>
                  );
                })}
                {/* TOPLAM Satırı */}
                <tr className="bg-gray-100 dark:bg-gray-600">
                  <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                    TOPLAM
                  </td>
                  {allMonths.map(month => {
                    const monthPaxTotal = hotelData
                      .filter(item => item.budget_month === month)
                      .reduce((sum, item) => sum + (parseFloat(item.pax) || 0), 0);
                    const monthAmountTotal = hotelData
                      .filter(item => item.budget_month === month)
                      .reduce((sum, item) => sum + (parseFloat(item.budget_amount) || 0), 0);

                    return (
                      <React.Fragment key={`total-${month}`}>
                        <td className="px-2 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-blue-100 dark:bg-blue-900/30">
                          {monthPaxTotal > 0 ? monthPaxTotal : '-'}
                        </td>
                        <td className="px-2 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600">
                          {formatCurrency(monthAmountTotal)}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  {/* Genel Toplam */}
                  <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-gray-200 dark:bg-gray-500">
                    {formatCurrency(hotelData.reduce((sum, item) => sum + (parseFloat(item.budget_amount) || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    });
  };

  // Dijital Pazarlama Ay Bazlı tablosu için özel render fonksiyonu
  const renderDigitalMonthTable = () => {
    if (!data || data.length === 0) return null;

    // Bölgelere göre grupla
    const groupedByRegion = {};
    data.forEach(item => {
      const region = item.region || 'Tanımsız Bölge';
      if (!groupedByRegion[region]) {
        groupedByRegion[region] = [];
      }
      groupedByRegion[region].push(item);
    });

    // Tüm benzersiz alt grupları al
    const allSubgroups = [...new Set(data.map(item => item.subgroup_name).filter(s => s))];
    
    // Ay isimleri
    const monthNames = {
      1: 'Ocak', 2: 'Şubat', 3: 'Mart', 4: 'Nisan', 5: 'Mayıs', 6: 'Haziran',
      7: 'Temmuz', 8: 'Ağustos', 9: 'Eylül', 10: 'Ekim', 11: 'Kasım', 12: 'Aralık'
    };

    return Object.keys(groupedByRegion).map(regionName => {
      // Bölgedeki verileri aya göre sırala (Ocak'tan Aralık'a)
      const regionData = groupedByRegion[regionName].sort((a, b) => {
        return (a.budget_month || 0) - (b.budget_month || 0);
      });

      return (
        <div key={regionName} className="mb-8">
          {/* Bölge Başlığı */}
          <div className="mb-4 pb-2 border-b-2 border-cyan-500 dark:border-cyan-400">
            <h3 className="text-xl font-bold text-cyan-600 dark:text-cyan-400 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              {regionName}
            </h3>
          </div>

          {/* Tablo */}
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className={`${colors.header}`}>
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                    Euro Kur / Ay
                  </th>
                  {allSubgroups.map(subgroup => (
                    <th key={subgroup} className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                      {subgroup}
                    </th>
                  ))}
                  <th className={`px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                    TOPLAM (€)
                  </th>
                  <th className={`px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                    TOPLAM (₺)
                  </th>
                </tr>
              </thead>
              <tbody>
                {regionData.map((row, index) => {
                  const eurCurr = row.eur_curr || 0;
                  const month = row.budget_month;
                  const monthName = monthNames[month] || `Ay ${month}`;
                  
                  // Bu satırdaki toplam euro
                  const rowTotalEur = parseFloat(row.budget_amount) || 0;
                  // Bu satırdaki toplam TL
                  const rowTotalTry = parseFloat(row.try_amount) || 0;

                  return (
                    <tr key={`${regionName}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm border border-gray-200 dark:border-gray-600">
                        <div className="text-center">
                          <div className="font-bold text-blue-600 dark:text-blue-400">
                            {eurCurr.toFixed(4)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {monthName}
                          </div>
                        </div>
                      </td>
                      {allSubgroups.map(subgroup => {
                        const amount = row.subgroup_name === subgroup ? (parseFloat(row.budget_amount) || 0) : 0;
                        return (
                          <td key={`${regionName}-${index}-${subgroup}`} className="px-4 py-3 text-sm text-center border border-gray-200 dark:border-gray-600">
                            <span className={amount > 0 ? 'font-semibold text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                              {amount > 0 ? formatCurrency(amount) : '-'}
                            </span>
                          </td>
                        );
                      })}
                      <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                        {formatCurrency(rowTotalEur)}
                      </td>
                      <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                        ₺{rowTotalTry.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}

                {/* TOPLAM Satırı */}
                <tr className="bg-gray-100 dark:bg-gray-600">
                  <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600">
                    TOPLAM
                  </td>
                  {allSubgroups.map(subgroup => {
                    const subgroupTotal = regionData
                      .filter(item => item.subgroup_name === subgroup)
                      .reduce((sum, item) => sum + (parseFloat(item.budget_amount) || 0), 0);
                    return (
                      <td key={`total-${subgroup}`} className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600">
                        {formatCurrency(subgroupTotal)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-gray-200 dark:bg-gray-500">
                    {formatCurrency(regionData.reduce((sum, item) => sum + (parseFloat(item.budget_amount) || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-gray-200 dark:bg-gray-500">
                    ₺{regionData.reduce((sum, item) => sum + (parseFloat(item.try_amount) || 0), 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    });
  };

       // Web Yazılım tablosu için özel render fonksiyonu (Subgrouplar satır, açıklama ve tutar dik)
       const renderWebSoftwareTable = () => {
         if (!data || data.length === 0) return null;

         // Reference data'dan tüm subgroup'ları al (Web Yazılım ana grubuna ait)
         const allSubgroups = referenceData?.subgroups?.filter(subgroup => 
           subgroup.maingroup_name === 'Web Yazılım'
         ).map(subgroup => subgroup.subgroup_name) || [];
         
         const subgroups = allSubgroups;
         
         // Her kayıt için ayrı satır oluştur (gruplama yapma)
         const tableData = data.map(item => ({
           id: item.id,
           subgroup_name: item.subgroup_name || '',
           summary: item.summary || '',
           budget_amount: parseFloat(item.budget_amount) || 0
         }));

         return (
           <div className="overflow-x-auto">
             <table className="w-full border-collapse">
               <thead>
                 <tr className="bg-gray-50 dark:bg-gray-700">
                   <th className="w-1/4 px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">ALT GRUP</th>
                   <th className="w-1/2 px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">AÇIKLAMA</th>
                   <th className="w-1/4 px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">TUTAR</th>
                 </tr>
               </thead>
               <tbody>
                 {tableData.map((item, index) => (
                   <tr key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                     <td className="w-1/4 px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                       {item.subgroup_name}
                     </td>
                     <td className="w-1/2 px-4 py-3 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                       {item.summary}
                     </td>
                     <td className={`w-1/4 px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 ${colors.total}`}>
                       {formatCurrency(item.budget_amount)}
                     </td>
                   </tr>
                 ))}
                 
                 {/* Toplam satırı */}
                 <tr className="bg-gray-100 dark:bg-gray-600">
                   <td colSpan="2" className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">TOPLAM</td>
                   <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-gray-200 dark:bg-gray-500">
                     {formatCurrency(tableData.reduce((sum, item) => sum + item.budget_amount, 0))}
                   </td>
                 </tr>
               </tbody>
             </table>
           </div>
         );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
      <div className={`${colors.header} text-gray-800 dark:text-gray-200 px-6 py-4`}>
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">{config.title}</h2>
        </div>
      </div>
      
      {displayMode === 'workshop_table' ? (
        renderWorkshopTable()
      ) : displayMode === 'promotion_table' ? (
        renderPromotionTable()
      ) : displayMode === 'fair_detail_table' ? (
        renderFairDetailTable()
      ) : displayMode === 'sales_call_table' ? (
        renderSalesCallTable()
      ) : displayMode === 'christmas_gift_table' ? (
        renderChristmasGiftTable()
      ) : displayMode === 'web_software_table' ? (
        renderWebSoftwareTable()
      ) : displayMode === 'represhos_table' ? (
        renderRepresHosTable()
      ) : displayMode === 'digital_month_table' ? (
        renderDigitalMonthTable()
      ) : displayMode === 'normal_table' || displayMode === 'generic_table' ? (
        renderNormalTable()
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                {rows.map(field => field.replace('_', ' ')).join(' - ')}
              </th>
              {pivotData.columnKeys.map(columnKey => (
                <th key={columnKey} className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600">
                  {columnKey.split('|').join(' - ')}
                </th>
              ))}
              <th className={`px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                TOPLAM
              </th>
            </tr>
          </thead>
          <tbody>
            {pivotData.rowKeys.map(rowKey => (
              <tr key={rowKey} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600">
                  {rowKey.split('|').join(' - ')}
                </td>
                {pivotData.columnKeys.map(columnKey => {
                  const cellData = pivotData.data[rowKey]?.[columnKey];
                  const amount = cellData?.value || 0;
                  
                  return (
                    <td 
                      key={columnKey}
                      className="px-4 py-4 text-sm text-center border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white"
                    >
                      <span className={
                        amount > 0 
                          ? 'font-semibold text-green-600 dark:text-green-400' 
                          : 'text-gray-400 dark:text-gray-500'
                      }>
                          {amount > 0 ? formatCurrency(amount) : '0 €'}
                        </span>
                      {renderAdditionalFields(rowKey, cellData)}
                    </td>
                  );
                })}
                <td className={`px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border border-gray-200 dark:border-gray-600 ${colors.total}`}>
                  {formatCurrency(rowTotals[rowKey])}
                </td>
              </tr>
            ))}
            {/* Grand Total Row */}
            <tr className="bg-gray-100 dark:bg-gray-600">
              <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                TOPLAM
              </td>
              {pivotData.columnKeys.map(columnKey => (
                <td key={columnKey} className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600">
                  {formatCurrency(columnTotals[columnKey] || 0)}
                </td>
              ))}
              <td className="px-4 py-3 text-sm font-bold text-gray-800 dark:text-gray-200 text-center border border-gray-200 dark:border-gray-600 bg-gray-200 dark:bg-gray-500">
                {formatCurrency(grandTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default GenericPivotTable;
